import React, { useState, useRef, useEffect } from 'react';
import { 
  Scale, Upload, Download, Copy, Check, 
  AlertCircle, FileText, ArrowLeft, RefreshCw, 
  Trash2, ShieldCheck, HelpCircle, Sparkles
} from 'lucide-react';

interface ContractAuditorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація аналітичного модуля...",
  "Старший експерт з бюджетного законодавства перевіряє відповідність Бюджетному кодексу України...",
  "Спеціаліст з ризиків вивчає умови пені, штрафів та терміни виконання зобов'язань...",
  "Провідний юрисконсульт структурує зауваження та готує таблицю рекомендацій...",
  "Формування остаточного юридичного висновку (Due Diligence)..."
];

const LOADING_STEPS_EN = [
  "Initializing AI agents...",
  "Senior budget law expert is verifying compliance with the Budget Code of Ukraine...",
  "Risk manager is analyzing penalties, liabilities, and fulfillment terms...",
  "Lead counsel is structuring findings and drafting the recommendations table...",
  "Compiling final legal opinion (Due Diligence report)..."
];

export default function ContractAuditor({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: ContractAuditorProps) {
  const isUk = language === 'uk';
  const [text, setText] = useState('');
  const [auditType, setAuditType] = useState<'budget' | 'commercial'>('budget');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  
  const [isExtracting, setIsExtracting] = useState(false);
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditReport, setAuditReport] = useState<string | null>(null);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cycle through loading steps during analysis
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000); // Shift every 7 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  // File Upload and Text Extraction
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setFileSize(file.size);
    setError(null);
    setIsExtracting(true);
    setAuditReport(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/extract-text', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка вилучення тексту з файлу.' : 'Failed to extract text from the file.'));
      }

      const data = await response.json();
      setText(data.text);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Не вдалося прочитати файл.' : 'Failed to read file.'));
      setFileName(null);
      setFileSize(null);
    } finally {
      setIsExtracting(false);
    }
  };

  // Run Contract Analysis
  const handleRunAudit = async () => {
    if (!text.trim()) return;

    setIsAuditing(true);
    setError(null);
    setAuditReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/audit-contract', {
        method: 'POST',
        headers,
        body: JSON.stringify({ text, useEnterpriseContext, auditType })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка аналізу договору аналітичною системою.' : 'Failed to audit contract.'));
      }

      const data = await response.json();

      if (data.report) {
        setAuditReport(data.report);
        setIsAuditing(false);
        return;
      }

      if (data.jobId) {
        const jobId = data.jobId;
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${jobId}`);
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              if (statusData.status === 'completed') {
                clearInterval(pollInterval);
                setAuditReport(statusData.report);
                setIsAuditing(false);
              } else if (statusData.status === 'failed') {
                clearInterval(pollInterval);
                setError(statusData.error || (isUk ? 'Помилка аналізу договору.' : 'Failed to audit contract.'));
                setIsAuditing(false);
              }
            }
          } catch (pollErr) {
            console.error('Polling error:', pollErr);
          }
        }, 4000);
      } else {
        setIsAuditing(false);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка під час аналізу договору.' : 'An error occurred during contract audit.'));
      setIsAuditing(false);
    }
  };

  // Download Audit Report as Word compatible .docx file
  const handleDownloadWord = async () => {
    if (!auditReport) return;

    try {
      const name = fileName ? 'audit-' + fileName.split('.')[0] : 'contract-audit-report';
      
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: auditReport,
          fileName: name
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate DOCX file.');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name}.docx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      alert(isUk ? 'Помилка при завантаженні файлу .docx' : 'Failed to download DOCX file');
    }
  };

  const handleCopyReport = () => {
    if (!auditReport) return;
    navigator.clipboard.writeText(auditReport);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  const handleClear = () => {
    setText('');
    setFileName(null);
    setFileSize(null);
    setAuditReport(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const loadingSteps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;

  return (
    <div className="space-y-6">
      {/* Upper Status Banner */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-xs">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
            title={isUk ? "Назад до Хабу" : "Back to Hub"}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="p-2 bg-emerald-50 text-emerald-600 rounded border border-emerald-100">
            <Scale className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-base font-bold font-mono uppercase tracking-wider text-slate-850">
              {isUk ? "Юридичний Аудитор" : "Contract Auditor"}
            </h2>
            <p className="text-[11px] text-slate-500 font-mono">
              {isUk ? "Мультиагентний аудит бюджетного та комерційного відповідності" : "Multi-agent budget and commercial contract audit"}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 self-stretch sm:self-auto flex-wrap">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
          >
            🏠 {isUk ? "На головну" : "Home"}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4 text-blue-600" />
            <span>{isUk ? "← До Бізнес Центру" : "← Back to Business Hub"}</span>
          </button>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">Активовано контекст підприємства: </span>
              <span className="font-mono">{enterpriseName || "База знань компанії"}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            База знань
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Side: File Upload or Input Text */}
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-lg p-5 shadow-xs space-y-4">
          <h3 className="text-xs font-bold font-mono text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2">
            {isUk ? "Завантаження договору" : "Upload Contract"}
          </h3>

          {/* Drag & Drop File Area */}
          <div className="relative">
            <input 
              type="file" 
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".docx,.pdf,.txt"
              className="hidden" 
              id="contract-file-upload"
              disabled={isExtracting || isAuditing}
            />
            <label 
              htmlFor="contract-file-upload"
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                isExtracting || isAuditing 
                  ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'
                  : 'border-slate-350 hover:border-emerald-500 hover:bg-emerald-50/20'
              }`}
            >
              {isExtracting ? (
                <>
                  <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin mb-2" />
                  <p className="text-xs font-mono text-slate-600">
                    {isUk ? "Зчитування та вилучення тексту..." : "Extracting document text..."}
                  </p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-400 mb-2" />
                  <p className="text-xs font-bold text-slate-700">
                    {isUk ? "Оберіть файл договору" : "Select contract file"}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-1 font-mono">
                    DOCX, PDF, TXT (max 5MB)
                  </p>
                </>
              )}
            </label>
          </div>

          {/* Quick Demo Sample Contract Button */}
          <button
            type="button"
            onClick={() => {
              setFileName("sample_contract_2026.docx");
              setFileSize(18420);
              setText(`ДОГОВІР ПОСТАВКИ № 12/2026-Б
м. Київ, 15 січня 2026 року

Постачальник: ТОВ "Прометей Технолоджіс", в особі директора Дикого О.В.
Покупець: Державна установа "Центр аналітики та моніторингу", в особі директора Петренка І.І.

1. ПРЕДМЕТ ДОГОВОРУ
1.1. Постачальник зобов'язується поставити Комп'ютерне обладнання (Код ДК 021:2015: 30210000-4 Машини для обробки даних), а Покупець прийняти та оплатити товар на умовах цього Договору.

2. СУМА ДОГОВОРУ ТА ПОРЯДОК РОЗРАХУНКІВ
2.1. Загальна вартість товару становить 450 000,00 грн. (чотириста п'ятдесят тисяч грн. 00 коп.), у т.ч. ПДВ 20%.
2.2. Оплата здійснюється після підписання Акту приймання-передачі протягом 30 календарних днів згідно з Бюджетним кодексом України при наявності бюджетного фінансування.

3. ПЕНЯ ТА ШТРАФНІ САНКЦІЇ
3.1. У разі затримки поставки Товару Постачальник сплачує Покупцю пеню в розмірі 0.1% від вартості непоставленого товару за кожен день прострочення.
3.2. Покупець не несе відповідальності за затримку розрахунків у разі затримки фінансування з Державного бюджету України.`);
              setError(null);
            }}
            className="w-full py-2 px-3 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-2xs"
          >
            <Sparkles className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Завантажити демо-договір для швидкого тесту" : "⚡ Load demo contract for 1-click test"}</span>
          </button>

          {/* Uploaded File Info */}
          {fileName && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 flex justify-between items-center">
              <div className="flex items-center gap-2.5 overflow-hidden">
                <FileText className="w-5 h-5 text-emerald-500 shrink-0" />
                <div className="overflow-hidden">
                  <p className="text-xs font-bold text-slate-800 truncate font-mono">{fileName}</p>
                  <p className="text-[10px] text-slate-500 font-mono">{fileSize ? formatBytes(fileSize) : ''}</p>
                </div>
              </div>
              <button 
                onClick={handleClear}
                disabled={isAuditing}
                className="p-1 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded transition-all cursor-pointer"
                title={isUk ? "Видалити файл" : "Remove file"}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Text Area Input */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold font-mono text-slate-500 uppercase tracking-wider block">
              {isUk ? "Або вставте текст договору вручну:" : "Or paste contract text manually:"}
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={isExtracting || isAuditing}
              placeholder={isUk ? "Вставте текст договору сюди..." : "Paste your contract text here..."}
              className="w-full h-80 px-3 py-2 text-xs font-mono border border-slate-300 rounded focus:outline-hidden focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 resize-y"
            />
          </div>

          {/* Audit Type Selection */}
          <div className="space-y-2 border-t border-slate-100 pt-3">
            <label className="text-[10px] font-bold font-mono text-slate-500 uppercase tracking-wider block">
              {isUk ? "Тип юридичного аудиту:" : "Legal Audit Type:"}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAuditType('budget')}
                disabled={isAuditing}
                className={`py-2 px-2.5 text-[10px] font-bold font-mono rounded border transition-all cursor-pointer text-center flex flex-col justify-center items-center gap-1 ${
                  auditType === 'budget'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-xs'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                <span>{isUk ? "🏛️ Бюджетний" : "🏛️ Budget"}</span>
                <span className="text-[8px] text-slate-400 font-normal normal-case">
                  {isUk ? "БКУ + Ризики" : "Budget Code + Risks"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setAuditType('commercial')}
                disabled={isAuditing}
                className={`py-2 px-2.5 text-[10px] font-bold font-mono rounded border transition-all cursor-pointer text-center flex flex-col justify-center items-center gap-1 ${
                  auditType === 'commercial'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-xs'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                <span>{isUk ? "💼 Комерційний" : "💼 Commercial"}</span>
                <span className="text-[8px] text-slate-400 font-normal normal-case">
                  {isUk ? "Тільки ризики" : "Risk Analysis Only"}
                </span>
              </button>
            </div>
          </div>

          {/* Audit Action Button */}
          <div className="pt-2">
            <button
              onClick={handleRunAudit}
              disabled={!text.trim() || isExtracting || isAuditing}
              className={`w-full py-2.5 text-xs font-mono font-bold rounded transition-all flex items-center justify-center gap-2 cursor-pointer uppercase ${
                !text.trim() || isExtracting || isAuditing
                  ? 'bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-xs'
              }`}
            >
              {isAuditing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {isUk ? "Аналізується аналітичними модулями..." : "Auditing via AI..."}
                </>
              ) : (
                <>
                  <Scale className="w-4 h-4" />
                  {isUk ? "Запустити аудит договору" : "Run Contract Audit"}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Right Side: Analysis Results */}
        <div className="lg:col-span-7 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 flex gap-3 items-start">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h4 className="text-xs font-bold font-mono uppercase tracking-wider">{isUk ? 'Помилка виконання' : 'Execution Error'}</h4>
                <p className="text-xs font-mono leading-relaxed">{error}</p>
              </div>
            </div>
          )}

          {isAuditing && (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center space-y-6 shadow-xs flex flex-col items-center justify-center min-h-[400px]">
              <div className="relative flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-slate-100 border-t-emerald-500 rounded-full animate-spin"></div>
                <Scale className="w-6 h-6 text-emerald-500 absolute animate-pulse" />
              </div>
              <div className="space-y-2 max-w-md">
                <h3 className="text-sm font-bold font-mono text-slate-800 uppercase tracking-wider">
                  {isUk ? "Триває мультиагентний аналіз" : "Multi-agent audit in progress"}
                </h3>
                <p className="text-xs text-slate-500 leading-relaxed font-mono min-h-[3rem] px-4">
                  {loadingSteps[loadingStepIndex]}
                </p>
                <div className="w-48 h-1 bg-slate-100 rounded-full mx-auto overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 transition-all duration-[7000ms] ease-out rounded-full"
                    style={{ width: `${((loadingStepIndex + 1) / loadingSteps.length) * 100}%` }}
                  />
                </div>
                <p className="text-[10px] text-slate-400 italic">
                  {isUk ? "Зазвичай це займає близько 30-40 секунд..." : "This usually takes about 30-40 seconds..."}
                </p>
              </div>
            </div>
          )}

          {!isAuditing && !auditReport && !error && (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center space-y-4 shadow-xs flex flex-col items-center justify-center min-h-[400px]">
              <div className="p-4 bg-slate-50 border border-slate-100 rounded-full text-slate-400">
                <Scale className="w-10 h-10" />
              </div>
              <div className="space-y-1.5 max-w-sm">
                <h3 className="text-xs font-bold font-mono text-slate-700 uppercase tracking-wider">
                  {isUk ? "Результат юридичного аналізу" : "Legal Audit Result"}
                </h3>
                <p className="text-xs text-slate-400 font-mono">
                  {isUk 
                    ? "Завантажте файл договору або вставте його текст ліворуч, щоб розпочати детальний аудит на відповідність БКУ та аналіз ризиків." 
                    : "Upload a contract file or paste its text on the left to start a detailed audit for budget compliance and risks."}
                </p>
              </div>
            </div>
          )}

          {auditReport && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-xs overflow-hidden flex flex-col">
              {/* Report Header Menu */}
              <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex flex-wrap justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  <span className="text-xs font-bold font-mono uppercase tracking-wider text-slate-700">
                    {isUk ? "Звіт: Юридичний висновок (Due Diligence)" : "Report: Legal Opinion (Due Diligence)"}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Copy Button */}
                  <button
                    onClick={handleCopyReport}
                    className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-200 rounded transition-all cursor-pointer flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase"
                    title={isUk ? "Копіювати звіт" : "Copy report"}
                  >
                    {copiedReport ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-emerald-700">{isUk ? "Скопійовано" : "Copied"}</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>{isUk ? "Копіювати" : "Copy"}</span>
                      </>
                    )}
                  </button>

                  {/* Download DOCX Button */}
                  <button
                    onClick={handleDownloadWord}
                    className="p-1.5 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded transition-all cursor-pointer flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase"
                    title={isUk ? "Завантажити як Word-файл" : "Download as Word file"}
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Word (.docx)</span>
                  </button>
                </div>
              </div>

              {/* Report Content */}
              <div className="p-6 overflow-y-auto max-h-[600px] prose prose-slate max-w-none text-xs font-mono leading-relaxed space-y-4">
                <div className="bg-emerald-50/30 border border-emerald-150 rounded-lg p-4 mb-4 text-[11px] text-emerald-850 flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold font-mono uppercase tracking-wider mb-1">
                      {isUk ? "Аудит завершено успішно" : "Audit Completed Successfully"}
                    </h4>
                    <p className="font-mono text-slate-655 leading-relaxed">
                      {isUk 
                        ? "ШІ-агенти проаналізували умови договору, виявили відповідність нормам БКУ та розрахували фінансово-юридичні ризики. Нижче наведено детальний юридичний висновок та рекомендації щодо внесення змін." 
                        : "AI agents successfully analyzed contract clauses, checked compliance with budget rules, and estimated commercial risks. A detailed Due Diligence report and redlines are presented below."}
                    </p>
                  </div>
                </div>

                {/* Preformatted text with markdown formatting rendered cleanly in monospaced style */}
                <div className="whitespace-pre-wrap select-text font-mono text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-4">
                  {auditReport}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
